/**
 * CommunityPage - Forum system for soap makers
 * 
 * Features:
 * - Forum groups (admin-controlled)
 * - Create posts with text, images, links
 * - Reply to posts
 * - Like/upvote posts
 */

import { sanitizeHtml } from '../utils/sanitizeHtml';
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  deleteDoc,
  doc, 
  serverTimestamp,
  where
} from 'firebase/firestore';
import { db, functions } from '../utils/firebase-config';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import RichTextEditor from '../Components/RichTextEditor';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import SignInPrompt from '../Components/SignInPrompt';
import './labcss/community-features.css';

// Predefined forum groups (admin creates these)
const FORUM_GROUPS = [
  { id: 'general', name: 'General Discussion', icon: '💬', description: 'Talk about anything soap related' },
  { id: 'recipes', name: 'Recipe Sharing', icon: '📖', description: 'Share and discuss soap recipes' },
  { id: 'troubleshooting', name: 'Troubleshooting', icon: '🔧', description: 'Get help with soap making problems' },
  { id: 'ingredients', name: 'Ingredients', icon: '🧴', description: 'Discuss oils, butters, additives' },
  { id: 'business', name: 'Business Talk', icon: '💼', description: 'Selling, pricing, and business advice' },
  { id: 'showcase', name: 'Showcase', icon: '✨', description: 'Show off your creations' },
  { id: 'beginners', name: 'Beginner Help', icon: '🌱', description: 'Questions from new soap makers' },
  { id: 'techniques', name: 'Techniques', icon: '🎨', description: 'Swirls, designs, and advanced methods' },
];

// Post Card Component
const PostCard = ({ post, currentUser, onLike, onReply, onDelete, onViewReplies, onSignIn }) => {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReplies, setShowReplies] = useState(false);

  const isAuthor = currentUser?.uid === post.authorId;
  const hasLiked = post.likedBy?.includes(currentUser?.uid);
  
  const [replying, setReplying] = useState(false);
  const handleSubmitReply = async () => {
    if (!replyText.trim() || replying) return;
    setReplying(true);
    try { if (await onReply(post.id, replyText)) { setReplyText(''); setShowReplyInput(false); } }
    finally { setReplying(false); }
  };

  return (
    <div className="frm-post-card">
      <div className="frm-post-head">
        <div className="frm-post-author">
          <div className="frm-avatar">
            {post.authorPhoto ? (
              <img src={post.authorPhoto} alt={post.authorName} />
            ) : (
              <span>{post.authorName?.charAt(0) || '?'}</span>
            )}
          </div>
          <div className="frm-author-meta">
            <span className="frm-author-name">{post.authorName || 'Anonymous'}</span>
            <span className="frm-post-time">
              {post.createdAt?.toDate?.()?.toLocaleDateString() || 'Just now'}
            </span>
          </div>
        </div>
        {isAuthor && (
          <button className="frm-delete-btn" onClick={() => onDelete(post.id)} title="Delete post">
            Delete
          </button>
        )}
      </div>

      <div className="frm-post-content">
        <h3 className="frm-post-title">{post.title}</h3>
        {/* Support both rich HTML and plain text content */}
        {post.content?.includes('<') ? (
          <div 
            className="frm-post-body frm-rich"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
          />
        ) : (
          <p className="frm-post-body">{post.content}</p>
        )}
        
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="frm-post-tags">
            {post.tags.map((tag, i) => (
              <span key={i} className="frm-tag">#{tag}</span>
            ))}
          </div>
        )}
        
        {/* Images */}
        {post.images && post.images.length > 0 && (
          <div className="frm-post-images">
            {post.images.map((img, i) => (
              <img key={i} src={img} alt={`Post image ${i + 1}`} />
            ))}
          </div>
        )}
        
        {/* Links */}
        {post.links && post.links.length > 0 && (
          <div className="frm-post-links">
            {post.links.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noopener noreferrer">
                {link.text || link.url}
              </a>
            ))}
          </div>
        )}
      </div>

      <div className="frm-actions">
        <button 
          className={`frm-action-btn ${hasLiked ? 'frm-liked' : ''}`}
          onClick={() => onLike(post.id, hasLiked)}
        >
          {hasLiked ? '♥' : '♡'} {post.likes || 0}
        </button>
        <button 
          className="frm-action-btn"
          onClick={() => (currentUser ? setShowReplyInput(!showReplyInput) : onSignIn('reply to posts'))}
        >
          Reply
        </button>
        {post.replyCount > 0 && (
          <button 
            className="frm-action-btn"
            onClick={() => setShowReplies(!showReplies)}
          >
            {post.replyCount} {post.replyCount === 1 ? 'Reply' : 'Replies'}
          </button>
        )}
      </div>

      {/* Reply Input */}
      {showReplyInput && (
        <div className="frm-reply-input">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply..."
            rows={3}
          />
          <div className="frm-reply-btns">
            <button onClick={() => setShowReplyInput(false)} className="frm-reply-cancel">Cancel</button>
            <button disabled={replying || !replyText.trim()} onClick={handleSubmitReply} className="frm-reply-submit">{replying ? 'Sending…' : 'Reply'}</button>
          </div>
        </div>
      )}

      {/* Replies */}
      {showReplies && post.replies && post.replies.length > 0 && (
        <div className="frm-replies">
          {post.replies.map((reply, i) => (
            <div key={i} className="frm-reply-item">
              <div className="frm-reply-head">
                <span className="frm-reply-author">{reply.authorName}</span>
                <span className="frm-reply-time">
                  {reply.createdAt?.toDate?.()?.toLocaleDateString() || 'Just now'}
                </span>
              </div>
              <p className="frm-reply-text">{reply.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Create Post Modal with Rich Text Editor
const CreatePostModal = ({ isOpen, onClose, onSubmit, selectedGroup }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [links, setLinks] = useState([]);
  const [linkInput, setLinkInput] = useState('');
  const [group, setGroup] = useState(selectedGroup || 'general');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  if (!isOpen) return null;

  const handleAddLink = () => {
    if (linkInput.trim()) {
      setLinks([...links, { url: linkInput, text: linkInput }]);
      setLinkInput('');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = () => {
    // Strip HTML tags for plain text check
    const plainText = content.replace(/<[^>]*>/g, '').trim();
    if (title.trim() && plainText) {
      onSubmit({
        title,
        content,
        links,
        group,
        tags,
      });
      setTitle('');
      setContent('');
      setLinks([]);
      setTags([]);
      onClose();
    }
  };

  return (
    <div className="frm-modal-overlay" onClick={onClose}>
      <div className="frm-modal" onClick={e => e.stopPropagation()}>
        <div className="frm-modal-head">
          <h2>Create New Post</h2>
          <button className="frm-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="frm-modal-body">
          {/* Group Selection */}
          <div className="frm-field">
            <label>Forum Group</label>
            <select value={group} onChange={(e) => setGroup(e.target.value)}>
              {FORUM_GROUPS.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div className="frm-field">
            <label>Post Title</label>
            <input
              type="text"
              className="frm-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's your post about?"
            />
          </div>

          {/* Rich Text Content */}
          <div className="frm-field">
            <label>Content</label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Share your thoughts, questions, or experiences..."
              minHeight="200px"
            />
          </div>

          {/* Tags */}
          <div className="frm-field">
            <label>Tags (helps others find your post)</label>
            <div className="frm-tags-box">
              {tags.map((tag, i) => (
                <span key={i} className="frm-tag-badge">
                  #{tag}
                  <button onClick={() => setTags(tags.filter((_, idx) => idx !== i))}>×</button>
                </span>
              ))}
              <input
                type="text"
                className="frm-tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Add tag..."
              />
            </div>
          </div>

          {/* Links */}
          <div className="frm-field">
            <label>Add Links (optional)</label>
            <div className="frm-link-row">
              <input
                type="url"
                value={linkInput}
                onChange={(e) => setLinkInput(e.target.value)}
                placeholder="https://..."
              />
              <button className="frm-link-add" onClick={handleAddLink} type="button">Add</button>
            </div>
            {links.length > 0 && (
              <div className="frm-link-list">
                {links.map((link, i) => (
                  <span key={i} className="frm-link-tag">
                    {link.url}
                    <button onClick={() => setLinks(links.filter((_, idx) => idx !== i))}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="frm-modal-foot">
          <button className="frm-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="frm-modal-submit" onClick={handleSubmit} disabled={!title.trim()}>
            Publish Post
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Community Page
export default function CommunityPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Load posts
  useEffect(() => {
    let q;
    if (selectedGroup === 'all') {
      q = query(collection(db, 'forumPosts'), orderBy('createdAt', 'desc'));
    } else {
      q = query(
        collection(db, 'forumPosts'), 
        where('group', '==', selectedGroup),
        orderBy('createdAt', 'desc')
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPosts(postsData);
      setLoading(false);
    }, (error) => { setLoading(false); console.error('Community load failed:', error); });

    return () => unsubscribe();
  }, [selectedGroup]);

  // Create post
  const handleCreatePost = async (postData) => {
    try {
      await addDoc(collection(db, 'forumPosts'), {
        ...postData,
        content: sanitizeHtml(postData.content),
        authorId: user.uid,
        authorName: user.displayName || user.email?.split('@')[0] || 'Anonymous',
        authorPhoto: user.photoURL || null,
        likes: 0,
        likedBy: [],
        replyCount: 0,
        replies: [],
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  // Only trusted callables update other authors' conversation metadata.
  const [signInPrompt, setSignInPrompt] = useState('');
  const handleLike = async (postId) => {
    if (!user) { setSignInPrompt('like posts'); return; }
    try { await httpsCallable(functions, 'toggleForumLike')({ postId }); }
    catch (error) { alert(error.message || 'Your like could not be saved.'); }
  };

  const handleReply = async (postId, replyText) => {
    if (!user) { setSignInPrompt('reply to posts'); return false; }
    try {
      await httpsCallable(functions, 'addForumReply')({ postId, content: replyText });
      return true;
    } catch (error) {
      alert(error.message || 'Your reply could not be saved.');
      return false;
    }
  };

  // Delete post
  const handleDelete = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deleteDoc(doc(db, 'forumPosts', postId));
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  // Filter posts by search
  const filteredPosts = posts.filter(post => 
    post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="sl-page forum-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Community Forum' }
          ]} />
          <div className="pageheading">Community Forum</div>
          <div className="pagedescription">Connect with fellow soap makers, share ideas, and get help.</div>
        </div>

        <div className="forum-page-content borderleft">
          <section className="mypadding borderbottom">
            <div className="frm-layout">
              {/* Sidebar - Groups */}
              <aside className="frm-sidebar">
                <div className="frm-sidebar-title">Groups</div>
                <button 
                  className={`frm-group-btn ${selectedGroup === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedGroup('all')}
                >
                  <span>All Posts</span>
                </button>
                {FORUM_GROUPS.map(group => (
                  <button 
                    key={group.id}
                    className={`frm-group-btn ${selectedGroup === group.id ? 'active' : ''}`}
                    onClick={() => setSelectedGroup(group.id)}
                  >
                    <span>{group.name}</span>
                  </button>
                ))}
              </aside>

              {/* Main Content - Posts */}
              <main>
                <div className="frm-posts-header">
                  <div className="frm-search">
                    <input
                      type="text"
                      placeholder="Search posts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <button 
                    className="frm-new-post-btn"
                    onClick={() => (user ? setShowCreateModal(true) : setSignInPrompt('start a conversation'))}
                  >
                    New Post
                  </button>
                </div>

                {loading ? (
                  <div className="frm-loading">Loading posts...</div>
                ) : filteredPosts.length === 0 ? (
                  <div className="frm-empty">
                    <h3>No posts yet</h3>
                    <p>Be the first to start a conversation!</p>
                    <button className="frm-empty-btn" onClick={() => (user ? setShowCreateModal(true) : setSignInPrompt('start a conversation'))}>Create Post</button>
                  </div>
                ) : (
                  <div>
                    {filteredPosts.map(post => (
                      <PostCard
                        key={post.id}
                        post={post}
                        currentUser={user}
                        onLike={handleLike}
                        onReply={handleReply}
                        onDelete={handleDelete}
                        onSignIn={setSignInPrompt}
                      />
                    ))}
                  </div>
                )}
              </main>
            </div>
          </section>
        </div>

        <CreatePostModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreatePost}
          selectedGroup={selectedGroup !== 'all' ? selectedGroup : 'general'}
        />
      <SignInPrompt intent={signInPrompt} onClose={() => setSignInPrompt('')} />
      <Footer />
      </div>
    </>
  );
}
